import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef, useState, useDeferredValue, useRef, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@skatteetaten/ds-buttons';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { FavoriteSVGpath, LogOutSVGpath } from '@skatteetaten/ds-icons';
import { Paragraph } from '@skatteetaten/ds-typography';
import { Modal, getModalDismissOnOutsideClickDefault, getModalDismissOnEscDefault } from '../Modal/index.esm.js';
import { RolePickerBusinessList } from '../RolePickerBusinessList/index.esm.js';
import { RolePickerContext } from '../RolePickerContext/index.esm.js';
import { RolePickerFilterInput } from '../RolePickerFilterInput/index.esm.js';
import { RolePickerPeopleList } from '../RolePickerPeopleList/index.esm.js';
import { RolePickerRow } from '../RolePickerRow/index.esm.js';
import '@skatteetaten/ds-forms';
import '@skatteetaten/ds-navigation';
import '@skatteetaten/ds-status';
import './styles.css';


const getRolePickerHideCloseButtonDefault = ()=>false;
const getRolePickerShowSubunitsDefault = ()=>true;
const getRolePickerShowInactiveBusinessesDefault = ()=>false;
const getRolePickerShowDeceasedPeopleDefault = ()=>false;

var styles = {
    "rolePicker": "RolePicker-module_rolePicker__pTz4X",
    "rolePickerFooter": "RolePicker-module_rolePickerFooter__64Pj-",
    "container": "RolePicker-module_container__VtHRL"};

const RolePicker = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, me, businesses, people, title, dismissOnEsc = getModalDismissOnEscDefault(), dismissOnOutsideClick = getModalDismissOnOutsideClickDefault(), hideCloseButton = getRolePickerHideCloseButtonDefault(), showInactiveBusinesses = getRolePickerShowInactiveBusinessesDefault(), showSubunits = getRolePickerShowSubunitsDefault(), showDeceasedPeople = getRolePickerShowDeceasedPeopleDefault(), onClose, onEntitySelect, onLogout, children }, ref)=>{
    const [filter, setFilter] = useState('');
    const [loadingEntityId, setLoadingEntityId] = useState(undefined);
    const [error, setError] = useState(undefined);
    const { t } = useTranslation('ds_overlays', {
        i18n: dsI18n
    });
    const deferredFilter = useDeferredValue(filter);
    const businessesCount = businesses ? businesses.total : 0;
    const peopleCount = people ? people.total : 0;
    const displaySearch = businessesCount + peopleCount > 10;
    const modalRef = useRef(null);
    useImperativeHandle(ref, ()=>modalRef.current);
    const handleClose = ()=>{
        setError(undefined);
        setLoadingEntityId(undefined);
        onClose?.();
    };
    const getRepresentationText = ()=>{
        if (people && people.total > 0 && businesses && businesses.total > 0) {
            return /*#__PURE__*/ jsxs(Paragraph, {
                children: [
                    t('rolepicker.YouCanRepresent'),
                    /*#__PURE__*/ jsx("strong", {
                        children: ` ${people.total} ${people.total > 1 ? t('rolepicker.People') : t('rolepicker.Person')} ${t('rolepicker.And')} ${businesses.total} ${businesses.total > 1 ? t('rolepicker.Businesses') : t('rolepicker.Business')}.`
                    })
                ]
            });
        } else if (people && people.total > 0) {
            return /*#__PURE__*/ jsxs(Paragraph, {
                children: [
                    t('rolepicker.YouCanRepresent'),
                    /*#__PURE__*/ jsx("strong", {
                        children: ` ${people.total} ${people.total > 1 ? t('rolepicker.People') : t('rolepicker.Person')}.`
                    })
                ]
            });
        } else if (businesses && businesses.total > 0) {
            return /*#__PURE__*/ jsxs(Paragraph, {
                children: [
                    t('rolepicker.YouCanRepresent'),
                    /*#__PURE__*/ jsx("strong", {
                        children: ` ${businesses.total} ${businesses.total > 1 ? t('rolepicker.Businesses') : t('rolepicker.Business')}.`
                    })
                ]
            });
        } else {
            return null;
        }
    };
    const concatenatedClassName = `${styles.container} ${className}`.trim();
    const noValidBusinesses = !me && !people && businesses && businesses.total === 0;
    let internalTitle = title ? title : t('rolepicker.Heading');
    if (noValidBusinesses) {
        internalTitle = t('rolepicker.NoBusinessesErrorTitle');
    }
    const contextValue = useMemo(()=>({
            onEntitySelect,
            error,
            setError,
            loadingEntityId,
            setLoadingEntityId
        }), [
        error,
        loadingEntityId,
        onEntitySelect
    ]);
    return /*#__PURE__*/ jsx(RolePickerContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ jsx(Modal, {
            ref: modalRef,
            id: id,
            className: concatenatedClassName,
            lang: lang,
            title: internalTitle,
            "data-testid": dataTestId,
            dismissOnEsc: dismissOnEsc,
            dismissOnOutsideClick: dismissOnOutsideClick,
            hideCloseButton: hideCloseButton,
            onClose: handleClose,
            children: /*#__PURE__*/ jsxs("div", {
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: styles.rolePicker,
                        children: [
                            me ? /*#__PURE__*/ jsx(RolePickerRow, {
                                id: me.personId,
                                title: t('rolepicker.MeHeading'),
                                description: `${t('rolepicker.PeopleDescriptionPrefix')} ${me.personId}`,
                                svgPath: FavoriteSVGpath,
                                titleAs: 'h2',
                                onClick: ()=>{
                                    setLoadingEntityId(me.personId);
                                    onEntitySelect?.(me).then((res)=>{
                                        if (res?.error) {
                                            setError({
                                                entityId: me.personId,
                                                message: res.error
                                            });
                                        }
                                        setLoadingEntityId(undefined);
                                    });
                                }
                            }) : null,
                            getRepresentationText(),
                            displaySearch ? /*#__PURE__*/ jsx(RolePickerFilterInput, {
                                label: people && businesses ? t('rolepicker.SearchAll') : people ? t('rolepicker.SearchPeople') : t('rolepicker.SearchBusinesses'),
                                value: filter,
                                onChange: (e)=>setFilter(e.target.value),
                                onClear: ()=>setFilter('')
                            }) : null,
                            people && people?.total > 0 ? /*#__PURE__*/ jsx(RolePickerPeopleList, {
                                people: people,
                                filterQuery: deferredFilter,
                                showDeceasedPeople: showDeceasedPeople
                            }) : null,
                            businesses && businesses.total > 0 ? /*#__PURE__*/ jsx(RolePickerBusinessList, {
                                businesses: businesses,
                                filterQuery: deferredFilter,
                                showSubunits: showSubunits,
                                showInactiveBusinesses: showInactiveBusinesses
                            }) : null,
                            noValidBusinesses ? /*#__PURE__*/ jsxs(Paragraph, {
                                children: [
                                    t('rolepicker.NoBusinessesDescription'),
                                    ' ',
                                    /*#__PURE__*/ jsxs("strong", {
                                        children: [
                                            t('rolepicker.Business'),
                                            '.'
                                        ]
                                    })
                                ]
                            }) : null,
                            children
                        ]
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: styles.rolePickerFooter,
                        children: noValidBusinesses ? /*#__PURE__*/ jsx(Button, {
                            href: `https://skatt.skatteetaten.no/web/minside/person`,
                            children: t('rolepicker.GoToMyPage')
                        }) : /*#__PURE__*/ jsxs(Fragment, {
                            children: [
                                /*#__PURE__*/ jsx(Button, {
                                    variant: 'secondary',
                                    svgPath: LogOutSVGpath,
                                    onClick: onLogout,
                                    children: t('rolepicker.Logout')
                                }),
                                !hideCloseButton ? /*#__PURE__*/ jsx(Button, {
                                    variant: 'secondary',
                                    onClick: ()=>modalRef.current?.close(),
                                    children: t('rolepicker.Cancel')
                                }) : null
                            ]
                        })
                    })
                ]
            })
        })
    });
});
RolePicker.displayName = 'RolePicker';

export { RolePicker, getRolePickerHideCloseButtonDefault, getRolePickerShowDeceasedPeopleDefault, getRolePickerShowInactiveBusinessesDefault, getRolePickerShowSubunitsDefault };
